export const YUZU_EMPTY_CARET_SENTINEL = '\u200B'
const YUZU_ZERO_WIDTH_CHARACTERS = /[\u200B-\u200D\uFEFF]/g

function isVisuallyEmpty(value: string) {
  return value
    .replace(YUZU_ZERO_WIDTH_CHARACTERS, '')
    .trim()
    .length === 0
}

export function isYuzuTextHtmlVisuallyEmpty(html: string) {
  const visibleText = html
    .replace(/<br\s*\/?\s*>/gi, '')
    .replace(/&nbsp;|&#160;|&#x0*a0;/gi, ' ')
    .replace(/<[^>]*>/g, '')

  return isVisuallyEmpty(visibleText)
}

function hasEmptyCaretSentinel(element: HTMLElement) {
  return element.childNodes.length === 1
    && element.textContent === YUZU_EMPTY_CARET_SENTINEL
}

function isElementVisuallyEmpty(element: HTMLElement) {
  return isVisuallyEmpty(element.textContent ?? '')
}

function ensureTrailingBreakCaretSentinel(element: HTMLElement) {
  const lastChild = element.lastChild
  if (lastChild instanceof Text
    && lastChild.data === YUZU_EMPTY_CARET_SENTINEL
    && lastChild.previousSibling instanceof HTMLElement
    && lastChild.previousSibling.tagName === 'BR') {
    return lastChild
  }
  if (!(lastChild instanceof HTMLElement) || lastChild.tagName !== 'BR') return null

  const sentinel = document.createTextNode(YUZU_EMPTY_CARET_SENTINEL)
  element.append(sentinel)
  return sentinel
}

function placeCaretAtEnd(element: HTMLElement) {
  const selection = window.getSelection()
  if (!selection) return

  const range = document.createRange()
  const trailingBreakSentinel = ensureTrailingBreakCaretSentinel(element)
  const trailingEmptyBlock = !trailingBreakSentinel
    && element.lastChild instanceof HTMLElement
    && isElementVisuallyEmpty(element.lastChild)
    ? element.lastChild
    : null
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT)
  let lastTextNode: Text | null = null
  let node = walker.nextNode()

  while (node) {
    lastTextNode = node as Text
    node = walker.nextNode()
  }

  if (trailingBreakSentinel) {
    range.setStart(trailingBreakSentinel, trailingBreakSentinel.length)
    range.collapse(true)
  }
  else if (trailingEmptyBlock) {
    range.setStart(trailingEmptyBlock, 0)
    range.collapse(true)
  }
  else if (lastTextNode) {
    range.setStart(lastTextNode, lastTextNode.length)
    range.collapse(true)
  }
  else {
    range.selectNodeContents(element)
    range.collapse(false)
  }
  selection.removeAllRanges()
  selection.addRange(range)
}

/** Adds a real line box so an empty contenteditable caret follows flex alignment. */
export function ensureYuzuEmptyCaretSentinel(element: HTMLElement) {
  if (!isElementVisuallyEmpty(element)) return false

  if (!hasEmptyCaretSentinel(element)) {
    element.replaceChildren(document.createTextNode(YUZU_EMPTY_CARET_SENTINEL))
  }
  placeCaretAtEnd(element)
  return true
}

export function focusYuzuTextEditorAtEnd(element: HTMLElement) {
  element.focus({ preventScroll: true })
  if (!ensureYuzuEmptyCaretSentinel(element)) placeCaretAtEnd(element)
}

export function clearYuzuEmptyCaretSentinel(element: HTMLElement) {
  if (!hasEmptyCaretSentinel(element)) return

  element.replaceChildren()
  placeCaretAtEnd(element)
}

/** Removes the empty-state sentinel after native input has committed. */
export function clearYuzuEmptyCaretSentinelAfterInput(element: HTMLElement) {
  if (!element.textContent?.includes(YUZU_EMPTY_CARET_SENTINEL)) return

  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT)
  const textNodes: Text[] = []
  let node = walker.nextNode()

  while (node) {
    textNodes.push(node as Text)
    node = walker.nextNode()
  }

  for (const textNode of textNodes) {
    if (!textNode.data.includes(YUZU_EMPTY_CARET_SENTINEL)) continue
    textNode.data = textNode.data.replaceAll(YUZU_EMPTY_CARET_SENTINEL, '')
    if (!textNode.data) textNode.remove()
  }

  placeCaretAtEnd(element)
}

export function normalizeYuzuTextEditorHtml(element: HTMLElement) {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT)
  let node = walker.nextNode()

  while (node) {
    node.textContent = node.textContent?.replaceAll(YUZU_EMPTY_CARET_SENTINEL, '') ?? ''
    node = walker.nextNode()
  }

  if (isElementVisuallyEmpty(element)) element.replaceChildren()
  return element.innerHTML
}
